import React from 'react';
import { Autocomplete, InputLabel, TextField } from '@mui/material';
import { CrmSegmentSelect } from './components/CrmSegmentSelect';
import { SearchBox } from './components/SearchBox';
import { useCrmStore } from './CreatorCrm.store';
import { useGetCrmDataQuery } from 'generated/graphql';
import { CrmSegment } from './CrmSegment.enum';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { useQuery } from '@tanstack/react-query';
import { getLeadsSources } from './CreatorCrm.api';
import { ExportButton } from './CrmExportButton';

type Option = {
  id: any;
  label: string;
};

type SelectProps = {
  value: Option | Option[] | null;
  onChange: (value: Option[]) => void | ((value: Option) => void);
  options: Option[];
  multiple?: boolean;
  width?: number;
};
function Select({
  value,
  onChange,
  options,
  multiple = false,
  width = 250,
}: SelectProps) {
  return (
    <Autocomplete
      multiple={multiple}
      value={value}
      onChange={(e, v) => onChange(v as Option[])}
      sx={{ width }}
      options={options}
      renderInput={(params) => <TextField {...params} size="small" />}
    />
  );
}

const selectValue = (
  options: Option[],
  value: string | string[],
  multiple = false,
) => {
  if (multiple) {
    return options.filter((o) => value.includes(o?.id));
  }

  return options.find((o) => o?.id === value) ?? null;
};

export function SelectSubscriptionStatus() {
  const crmStore = useCrmStore();

  const subscriptionOptions = [
    { id: 'Cancelled', label: 'Cancelled' },
    { id: 'Active', label: 'Active' },
    { id: 'Inactive', label: 'Inactive' },
    { id: 'BillingFailed', label: 'Billing Failure' },
    crmStore.segment === CrmSegment.ExclusiveBuyers && {
      id: 'None',
      label: 'None',
    },
  ].filter((x) => x) as Option[];

  return (
    <InputLabel>
      <b>Subscription</b>
      <Select
        options={subscriptionOptions}
        value={selectValue(
          subscriptionOptions,
          crmStore.filters.subscription,
          true,
        )}
        onChange={(options: Option[]) =>
          crmStore.handleFilterChange(
            'subscription',
            options.map((option) => option.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectClassification() {
  const crmStore = useCrmStore();

  const classificationOptions = [
    { id: 'MigratedSubscription', label: 'Migrated' },
    { id: 'FreeSubscription', label: 'Free' },
    { id: 'PaidSubscription', label: 'Paid' },
    { id: 'TrialSubscription', label: 'Trial' },
    { id: 'BulkUpdateSubscription', label: 'Bulk Uploads' },
  ];

  return (
    <InputLabel>
      <b>Classification</b>
      <Select
        options={classificationOptions}
        value={selectValue(
          classificationOptions,
          crmStore.filters.classification,
          true,
        )}
        onChange={(options: Option[]) =>
          crmStore.handleFilterChange(
            'classification',
            options.map((option) => option.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectSubscriptionType() {
  const crmStore = useCrmStore();
  const creatorId = useInfluencerId();
  const result = useGetCrmDataQuery({
    variables: {
      creatorId,
    },
  });

  const visibility = result.data?.creator?.visibility;

  if (!visibility) {
    return null;
  }

  const VISIBILITY_TO_OPTIONS: any = {
    premium: [
      { id: 'PremiumMonthly', label: 'Premium Monthly' },
      { id: 'PremiumAnnual', label: 'Premium Annual' },
    ],
    exclusive: [
      { id: 'ExclusiveMonthly', label: 'Exclusive Monthly' },
      { id: 'ExclusiveAnnual', label: 'Exclusive Annual' },
    ],
    limited: [
      { id: 'LimitedMonthly', label: 'Limited Monthly' },
      { id: 'LimitedAnnual', label: 'Limited Annual' },
      { id: 'PremiumMonthly', label: 'Premium Monthly' },
      { id: 'PremiumAnnual', label: 'Premium Annual' },
    ],
  };

  return (
    <InputLabel>
      <b>Type</b>
      <Select
        options={VISIBILITY_TO_OPTIONS[visibility]}
        value={selectValue(
          VISIBILITY_TO_OPTIONS[visibility],
          crmStore.filters.type,
          true,
        )}
        onChange={(options: Option[]) =>
          crmStore.handleFilterChange(
            'type',
            options.map((option) => option.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectAdditionalLabel() {
  const crmStore = useCrmStore();

  const additionalLabels = [
    { id: 'Recently converted', label: 'Recently converted' },
    { id: 'Recently churned', label: 'Recently churned' },
    { id: 'First purchase', label: 'First purchase' },
  ];

  return (
    <InputLabel>
      <b>Additional</b>
      <Select
        options={additionalLabels}
        value={selectValue(additionalLabels, crmStore.filters.additional, true)}
        onChange={(options: Option[]) =>
          crmStore.handleFilterChange(
            'additional',
            options.map((option) => option.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectOTPs() {
  const crmStore = useCrmStore();
  const creatorId = useInfluencerId();
  const result = useGetCrmDataQuery({
    variables: {
      creatorId,
    },
  });

  const otpOptions: Option[] =
    result.data?.creator?.publishedPrograms
      ?.filter((item) => item.accessType === 'exclusive')
      .map((p) => ({
        id: p.id,
        label: String(p.name),
      })) ?? [];

  return (
    <InputLabel>
      <b>OTP</b>
      <Select
        options={otpOptions}
        value={selectValue(otpOptions, crmStore.filters.programId, true)}
        onChange={(options: Option[]) =>
          crmStore.handleFilterChange(
            'programId',
            options.map((option) => option.id),
          )
        }
        width={300}
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectLeadsSource() {
  const crmStore = useCrmStore();
  const { data } = useQuery(['crm', 'leadsSources'], getLeadsSources);

  const leadSources = data ?? [];

  const leadsSourcesOptions: Option[] = [];

  leadSources.forEach((ls) =>
    leadsSourcesOptions.push({
      id: ls.value,
      label: ls.title,
    }),
  );

  return (
    <InputLabel>
      <b>Source</b>
      <Select
        width={200}
        options={leadsSourcesOptions}
        value={selectValue(
          leadsSourcesOptions,
          crmStore.filters.leadsSource,
          true,
        )}
        onChange={(options: Option[]) =>
          crmStore.handleFilterChange(
            'leadsSource',
            options.map((option) => option.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

const saveAsHash = (fn: any) => (v: any) => {
  window.location.hash = v;
  return fn(v);
};

type FiltersBarProps = {
  totalResults: number;
  children: React.ReactNode;
};
export function FiltersBar({ children, totalResults }: FiltersBarProps) {
  const crmStore = useCrmStore();

  return (
    <div className="flex flex-wrap gap-5 md:w-full">
      <section>
        <InputLabel>
          <b>Segments</b>
          <CrmSegmentSelect
            activeFilter={crmStore.segment}
            setActiveFilter={saveAsHash(crmStore.setActiveSegment)}
          />
        </InputLabel>
      </section>
      <section className="w-full md:w-auto flex flex-col md:flex-row gap-4">
        {children}
      </section>
      <section className="w-full md:w-auto flex items-start gap-4 justify-between">
        <div className="w-full md:w-auto min-w-[200px]">
          <InputLabel>
            <span>
              <b>Search</b>
              <span className="ml-2 text-[#8A8E99]">
                {totalResults} Results
              </span>
            </span>
            <SearchBox
              onChange={crmStore.setSearchTerm}
              value={crmStore.searchTerm}
            />
          </InputLabel>
        </div>

        <ExportButton />
      </section>
    </div>
  );
}
